"use client";

/**
 * Orik's occupants of the harness shell's seats: the sidebar's brand and account launcher, Orik's Settings pages,
 * and the dialogs Orik raises over the frame.
 *
 * The frame, the sidebar, the Settings panel and the theme are the harness's own (`ui-layout`, `ui-sidebar`,
 * `ui-settings-general`, `ui-theme`); these components fill the seats they leave for a deployment, and draw with the
 * harness's controls (`harness/primitives.ts`). Registration lives in `harness/orik-shell.ts`.
 */
import { writeSignedInHint } from "@/lib/signed-in-hint";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { LogOutIcon } from "lucide-react";
import { harnessPrimitives } from "../../harness/primitives";
import { OrikMark } from "@/components/brand";
import { initialsOf, useAccount } from "@/lib/account";
import { authClient } from "@/lib/auth-client";
import { useDeleteConversation } from "@/lib/delete-conversation";
import { request } from "@/lib/json-request";
import { hasPendingSettingsSection, takePendingSettingsSection } from "@/lib/agent/shell-links";

/** The brand mark in the sidebar's brand row and its collapsed rail. */
export function OrikBrandMark({ size }: { readonly size: number }): ReactNode {
  return <span data-orik-ui="" className="flex items-center justify-center text-primary" style={{ width: size, height: size }}>
    <OrikMark style={{ width: size, height: size }} />
  </span>;
}

/** The product name beside it. */
export function OrikBrandName(): ReactNode {
  return <span data-orik-ui="" className="font-sans text-[17px] font-semibold tracking-[-0.03em] text-foreground">orik.sh</span>;
}

/** The step that opens the Settings section a page load asked for (`src/lib/agent/shell-links.ts`). */
export const SETTINGS_LINK_STEP = "orik-settings-link";

/** What the settings shell hands its launcher. */
interface LauncherProps {
  readonly wide: boolean;
  readonly settingsOpen: boolean;
  openSettings(): void;
  openOnboarding(id: string): void;
}

/**
 * The sidebar's account launcher: who is signed in, opening Settings, where the account and sign-out live.
 *
 * It takes the plain Settings trigger's place at the foot of the sidebar. A page load that asked for a Settings
 * section (`/settings`, where account connections return) opens it through {@link SETTINGS_LINK_STEP}, the one
 * route by which the settings shell lets a registrant open a section.
 */
export function OrikLauncher({ wide, settingsOpen, openSettings, openOnboarding }: LauncherProps): ReactNode {
  const user = useAccount(state => state.user) ?? "";
  const asked = useRef(false);
  useEffect(() => {
    if (asked.current || !hasPendingSettingsSection()) return;
    asked.current = true;
    openOnboarding(SETTINGS_LINK_STEP);
  }, [openOnboarding]);
  return <button type="button" data-orik-ui="" onClick={openSettings} aria-haspopup="dialog" aria-expanded={settingsOpen}
    aria-label={wide ? undefined : `Settings for ${user || "your account"}`} title={wide ? undefined : user}
    className={`flex min-w-0 items-center gap-2.5 rounded-lg font-sans text-foreground transition-colors hover:bg-[var(--dsw-alias-interactive-bg-hover)] focus-visible:outline-2 focus-visible:outline-ring ${wide ? "h-11 flex-1 px-2" : "size-9 justify-center"}`}>
    <span aria-hidden="true" className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[11px] font-semibold text-primary">{initialsOf(user)}</span>
    {wide && <span className="flex min-w-0 flex-col items-start text-left leading-tight">
      <span className="w-full truncate text-[13px] font-medium" title={user}>{user}</span>
      <span className="text-[11px] text-muted-foreground">Settings</span>
    </span>}
  </button>;
}

/** The settings-link step: open the section the page load asked for, then hand the stage on. */
export function OrikSettingsLinkStep({ complete, openSection }: { complete(): void; openSection(id: string): void }): null {
  useEffect(() => {
    const section = takePendingSettingsSection();
    if (section !== undefined) openSection(section);
    complete();
  }, [complete, openSection]);
  return null;
}

/** The Account page: who is signed in, and signing out. */
export function AccountSection(): ReactNode {
  const { Button } = harnessPrimitives();
  const user = useAccount(state => state.user) ?? "";
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const signOut = async (): Promise<void> => {
    setBusy(true); setError("");
    const result = await authClient.signOut();
    if (result.error) { setError(result.error.message ?? "Sign out failed."); setBusy(false); return; }
    writeSignedInHint(undefined);
    window.location.assign("/");
  };
  return <section data-orik-ui="" aria-label="Account" className="flex flex-col gap-4 font-sans text-foreground">
    <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-5">
      <span aria-hidden="true" className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/15 text-sm font-semibold text-primary">{initialsOf(user)}</span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium" title={user}>{user}</p>
        <p className="text-xs text-muted-foreground">Signed in with Microsoft</p>
      </div>
      <Button variant="outline" icon={<LogOutIcon className="size-4" aria-hidden />} disabled={busy} onClick={() => { void signOut(); }}>Sign out</Button>
    </div>
    {error && <p role="alert" className="text-[13px] text-destructive">{error}</p>}
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <OrikMark className="size-4 text-primary" />
      <span>orik.sh</span>
    </div>
  </section>;
}

/** The confirmation for a Delete chosen in a conversation row's menu (`harness/orik-harness.ts`). */
export function DeleteConversationDialog(): ReactNode {
  const { Button, Modal } = harnessPrimitives();
  const pending = useDeleteConversation(state => state.pending);
  const clear = useDeleteConversation(state => state.clear);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const close = useCallback(() => { if (!busy) { setError(""); clear(); } }, [busy, clear]);
  const confirm = async (): Promise<void> => {
    if (pending === undefined) return;
    setBusy(true); setError("");
    try {
      await request(`/api/sessions/${encodeURIComponent(pending.sessionId)}`, { method: "DELETE" });
      clear();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not delete the conversation. Try again.");
    } finally { setBusy(false); }
  };
  return <Modal open={pending !== undefined} onClose={close} closeLabel="Close"
    title={`Delete “${pending?.title || "this conversation"}”?`}
    description="Its running work is stopped and its sandbox and browser state are removed. It no longer appears in the list."
    footer={<span data-orik-ui="" className="flex justify-end gap-2">
      <Button variant="outline" disabled={busy} onClick={close}>Cancel</Button>
      <Button variant="primary" disabled={busy} onClick={() => { void confirm(); }}>{busy ? "Deleting…" : "Delete conversation"}</Button>
    </span>}>
    {error && <p role="alert" data-orik-ui="" className="font-sans text-[13px] text-destructive">{error}</p>}
  </Modal>;
}
